import type React from 'react';
import { useMemo } from 'react';

import { cn } from '@/utils';

import { inlineLink } from './Poll.css';
import type { PollChoiceType, PollStyle } from './Poll.types';

interface Props {
  choice: PollChoiceType;
  pollStyle: PollStyle;
  isLast: boolean;
  isInteractive?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  onVote?: () => void;
}

const defaultContainerStyle: React.CSSProperties = {
  border: '1px solid rgba(50, 50, 50, 0.17)',
  padding: '6px 12px',
  borderRadius: '4px',
};

export const PollChoice = ({ choice, pollStyle, isLast, isInteractive, isLoading, isDisabled, onVote }: Props) => {
  const isInline = pollStyle === 'inline';
  const isDefault = pollStyle === 'default';

  const linkColor = 'var(--poll-link-color, inherit)';

  const linkStyle = useMemo<React.CSSProperties>(() => {
    if (isDefault) {
      return { color: linkColor, textDecoration: 'none', fontSize: '14px' };
    }
    if (pollStyle === 'unstyled') {
      return { color: linkColor };
    }
    return { color: linkColor, textDecoration: 'underline' };
  }, [pollStyle, isDefault]);

  if (isInteractive) {
    return (
      <li className={cn(!isDefault && !isInline && inlineLink)} style={isDefault ? defaultContainerStyle : undefined}>
        <button
          type="button"
          onClick={onVote}
          disabled={isDisabled}
          style={{
            ...linkStyle,
            cursor: isDisabled ? 'wait' : 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            opacity: isLoading ? 0.5 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          {choice.label}
        </button>
        {!isLast && isInline && <span style={{ color: 'inherit' }}>{'\u00a0|\u00a0'}</span>}
      </li>
    );
  }

  return (
    <li className={cn(!isDefault && !isInline && inlineLink)} style={isDefault ? defaultContainerStyle : undefined}>
      <a
        data-skip-tracking="true"
        rel="noopener noreferrer nofollow"
        style={linkStyle}
        target="_blank"
        href={choice.response_url}
      >
        {choice.label}
      </a>
      {!isLast && isInline && <span style={{ color: 'inherit' }}>{'\u00a0|\u00a0'}</span>}
    </li>
  );
};
